'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listStreamingPlatforms,
  createStreamingPlatform,
  updateStreamingPlatform,
  deleteStreamingPlatform,
  type CreateStreamingPlatformInput,
  type UpdateStreamingPlatformInput,
} from '@nomiss/services';

export const adminStreamingPlatformsQueryKeys = {
  all: ['admin', 'streaming-platforms'] as const,
  list: () => [...adminStreamingPlatformsQueryKeys.all, 'list'] as const,
};

export function useAdminStreamingPlatforms(options?: { enabled?: boolean }) {
  const adminStreamingPlatformsQuery = useQuery({
    queryKey: adminStreamingPlatformsQueryKeys.list(),
    queryFn: () => listStreamingPlatforms(),
    enabled: options?.enabled ?? true,
  });

  return { adminStreamingPlatformsQuery };
}

export function useCreateStreamingPlatform() {
  const queryClient = useQueryClient();

  const createStreamingPlatformMutation = useMutation({
    mutationFn: (input: CreateStreamingPlatformInput) =>
      createStreamingPlatform(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminStreamingPlatformsQueryKeys.list(),
      });
    },
  });

  return { createStreamingPlatformMutation };
}

export function useUpdateStreamingPlatform() {
  const queryClient = useQueryClient();

  const updateStreamingPlatformMutation = useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: UpdateStreamingPlatformInput;
    }) => updateStreamingPlatform(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminStreamingPlatformsQueryKeys.list(),
      });
    },
  });

  return { updateStreamingPlatformMutation };
}

export function useDeleteStreamingPlatform() {
  const queryClient = useQueryClient();

  const deleteStreamingPlatformMutation = useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      deleteStreamingPlatform(id, force),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminStreamingPlatformsQueryKeys.list(),
      });
    },
  });

  return { deleteStreamingPlatformMutation };
}
